/**
 * Todo o TEXTO da landing pública (rizoma.farm) mora aqui — a página só monta.
 *
 * Por que separado: a copy vai ser revisada mais de uma vez antes e depois da
 * abertura (números de uso, perguntas, a página do corretor). Mudar uma frase não
 * deve exigir ler JSX, e o teste de render (`pages/landing.test.ts`) lê daqui o
 * que precisa estar na tela.
 *
 * ⚠️ Regras de conteúdo do lançamento (não reabrir sem o Hugo):
 *   - Gratuito para produtores e compradores. Nada de preço, plano ou checkout.
 *   - O site NÃO cita a fazenda de origem nem nomes reais. "Criado por um produtor
 *     de banana" + os números de uso. Exemplos SÓ com nomes fictícios: Fazenda
 *     São José, João (produtor), Marcos (comprador).
 *   - Texto que imita mensagem de WhatsApp segue a regra do app: BMP-only
 *     (▸ • —), nunca emoji fora do BMP — é o que o WhatsApp do Rizoma consegue mandar.
 */

// ─── Números de uso real ──────────────────────────────────────────────────────
// ATUALIZAR ANTES DE PUBLICAR. É o único lugar: a faixa de prova lê daqui.
export const NUMEROS_DE_USO = [
  { valor: "+79 mil", rotulo: "mangarás marcados" },
  { valor: "+50 mil", rotulo: "cachos colhidos" },
  { valor: "~29 mil", rotulo: "cachos previstos para colher" },
] as const;

// ─── Contato do corretor ──────────────────────────────────────────────────────

export const TEXTO_WHATSAPP_CORRETOR =
  "Olá! Sou corretor/distribuidor e quero usar o Rizoma com meus produtores.";

/**
 * O destino do botão "Quero usar com meus produtores".
 *
 * Com número (`VITE_CONTATO_WHATSAPP`, só dígitos com DDI e DDD): `wa.me` com o
 * texto pronto. SEM número, o botão leva às perguntas frequentes — nunca a um
 * número inventado nem a um `wa.me/` vazio, que abre o WhatsApp sem destinatário
 * e faz o corretor achar que mandou.
 */
export function linkContatoCorretor(numero: string | undefined | null): string {
  const digitos = (numero ?? "").replace(/\D/g, "");
  // 10 dígitos é o menor número brasileiro com DDD sem o 55; menos que isso é lixo.
  if (digitos.length < 10) return "#perguntas";
  return `https://wa.me/${digitos}?text=${encodeURIComponent(TEXTO_WHATSAPP_CORRETOR)}`;
}

// ─── Navegação ────────────────────────────────────────────────────────────────

export const LINKS_NAV = [
  { href: "#modulos", rotulo: "Para o produtor" },
  { href: "#corretor", rotulo: "Para corretores" },
  { href: "#jornada", rotulo: "Como funciona" },
  { href: "#perguntas", rotulo: "Perguntas" },
] as const;

// ─── Hero ─────────────────────────────────────────────────────────────────────

export const HERO = {
  eyebrow: "Gestão da fazenda e da venda, no celular",
  tituloInicio: "Da colheita ao pagamento,",
  tituloDestaque: "tudo combinado e registrado.",
  subtitulo:
    "O produtor registra a colheita e a venda; o comprador confere o romaneio, monta a carga e registra o pagamento. Cada um vê a mesma conta, e o aviso chega pelo WhatsApp.",
  caminhos: [
    {
      id: "produtor",
      titulo: "Sou produtor",
      texto: "Controle a colheita, envie o romaneio e saiba o que tem a receber.",
      href: "/signup",
      interno: true,
    },
    {
      id: "corretor",
      titulo: "Sou corretor ou distribuidor",
      texto: "Receba os romaneios dos seus produtores e organize cargas e pagamentos.",
      href: "#corretor",
      interno: false,
    },
  ],
  gratuito: "Gratuito para produtores e compradores. Sem cartão de crédito.",
} as const;

// Os dois celulares do hero — exemplo fictício (João vende para Marcos).
export const DEMO_VENDA = {
  titulo: "Venda nº 142",
  estado: "Enviada",
  comprador: "Marcos",
  vendedor: "Fazenda São José",
  linhas: [
    { descricao: "Nanica 1ª", detalhe: "4.800 kg × R$ 1,20", valor: "R$ 5.760,00" },
    { descricao: "Nanica 2ª", detalhe: "1.200 kg × R$ 0,70", valor: "R$ 840,00" },
  ],
  total: "R$ 6.600,00",
  vencimento: "Vence sex, 14/11",
  acao: "Enviar pelo WhatsApp",
} as const;

export const DEMO_AVISO = {
  hora: "07:00",
  data: "quinta-feira, 13 de novembro",
  app: "Rizoma",
  quando: "agora",
  titulo: "Vence amanhã: romaneio nº 142",
  texto: "Marcos — R$ 6.600,00. Toque para ver a venda.",
  segundo: { titulo: "Carga conferida", texto: "Marcos confirmou 6.000 kg da Fazenda São José." },
} as const;

// ─── Faixa de prova ───────────────────────────────────────────────────────────

export const PROVA =
  "Criado por um produtor de banana, para resolver o dia a dia da própria fazenda. Estes são os números de uso real até aqui.";

// ─── Produção e vendas ────────────────────────────────────────────────────────

export const MODULOS = {
  titulo: "Produção e vendas no mesmo lugar",
  subtitulo: "Dois módulos que conversam: o que sai do bananal já entra na venda.",
  cartoes: [
    {
      id: "cacho",
      nome: "Cacho",
      resumo: "Produção",
      texto:
        "Marque os mangarás com a fita da semana, registre a colheita no campo e saiba o que vai estar no ponto em cada setor.",
      itens: [
        "Mangarás e fitas por cor, setor a setor",
        "Colheita e perdas registradas pelo celular",
        "Previsão semanal do que colher",
      ],
    },
    {
      id: "mercado",
      nome: "Mercado",
      resumo: "Vendas",
      texto:
        "Monte o romaneio, envie ao comprador por link e acompanhe o que falta receber. O que foi combinado fica escrito para os dois lados.",
      itens: [
        "Romaneio com totais conferidos",
        "Link para o comprador, sem instalar nada",
        "A receber e vencimentos, com aviso",
      ],
    },
  ],
  convite:
    "Equipe (diárias e folha) e Manejo (operações e irrigação) já estão em uso e são liberados sob convite.",
} as const;

// ─── Como funciona ────────────────────────────────────────────────────────────

export type LadoDoPasso = "Produtor" | "Comprador";

export const JORNADA: {
  titulo: string;
  subtitulo: string;
  passos: ReadonlyArray<{ titulo: string; texto: string; lado: LadoDoPasso }>;
} = {
  titulo: "Como funciona",
  subtitulo: "Uma venda, do bananal ao pagamento, em cinco passos.",
  passos: [
    {
      titulo: "Colhe e vende",
      texto: "O produtor registra a colheita e monta a venda com peso, classificação e preço.",
      lado: "Produtor",
    },
    {
      titulo: "Envia num toque",
      texto: "O romaneio sai por link no WhatsApp. O comprador abre no celular.",
      lado: "Produtor",
    },
    {
      titulo: "Comprador confere",
      texto: "Confere peso e valores e confirma o que recebeu.",
      lado: "Comprador",
    },
    {
      titulo: "Monta a carga",
      texto: "Junta romaneios de vários produtores numa carga só.",
      lado: "Comprador",
    },
    {
      titulo: "Paga e avisa",
      texto: "Registra o pagamento e o produtor recebe o aviso na hora.",
      lado: "Comprador",
    },
  ],
};

// ─── No WhatsApp ──────────────────────────────────────────────────────────────

export const WHATSAPP = {
  titulo: "O aviso certo, no WhatsApp",
  subtitulo: "Sem abrir o app para saber o que mudou: o resumo e os avisos chegam na conversa.",
  conversas: [
    {
      id: "resumo",
      para: "Para o produtor",
      linhas: [
        "▸ Resumo da semana — Fazenda São José",
        "• 1.240 cachos colhidos",
        "• 3 vendas enviadas — R$ 18.450,00",
        "• A receber até sexta: R$ 6.600,00",
      ],
    },
    {
      id: "pagamento",
      para: "Para o produtor",
      linhas: [
        "▸ Pagamento registrado",
        "Marcos pagou o romaneio nº 142 — R$ 6.600,00, via Pix.",
        "Nada mais em aberto com Marcos.",
      ],
    },
    {
      id: "romaneios",
      para: "Para o comprador",
      linhas: [
        "▸ 3 romaneios novos para conferir",
        "• Fazenda São José — 6.000 kg",
        "• Sítio Pedra Branca — 2.400 kg",
        "• Fazenda Santa Luzia — 3.800 kg",
      ],
    },
  ],
} as const;

// ─── Corretores e distribuidores ──────────────────────────────────────────────

export const CORRETOR = {
  eyebrow: "Para corretores e distribuidores",
  titulo: "Os romaneios dos seus produtores, num lugar só.",
  beneficios: [
    "O romaneio chega pronto: peso, classificação e preço combinados, sem redigitar.",
    "Cargas montadas com romaneios de vários produtores, com o total do caminhão.",
    "A pagar por produtor e por vencimento, e o produtor avisado quando você paga.",
    "O acesso nasce do primeiro romaneio que você recebe: entra com o telefone.",
  ],
  entrar: "Já recebi um romaneio — entrar",
  contato: "Quero usar com meus produtores",
  video: { titulo: "Como usar em 90 segundos", estado: "vídeo em produção" },
} as const;

// ─── Gratuito ─────────────────────────────────────────────────────────────────

export const GRATUITO = {
  titulo: "Gratuito para produtores e compradores",
  texto:
    "O Rizoma nasceu para organizar uma fazenda de banana e agora está aberto para quem produz e para quem compra. Crie a conta e comece hoje.",
  itens: [
    "Produção e vendas, sem limite de usuários",
    "Avisos pelo WhatsApp incluídos",
    "Sem cartão de crédito para começar",
  ],
} as const;

// ─── Perguntas frequentes ─────────────────────────────────────────────────────
// As duas primeiras abrem sozinhas (`aberta`).

export const PERGUNTAS = [
  {
    pergunta: "É gratuito mesmo?",
    resposta:
      "Sim. Produção e vendas são gratuitos para produtores e compradores, sem cartão de crédito e sem limite de usuários.",
    aberta: true,
  },
  {
    pergunta: "O comprador precisa criar conta?",
    resposta:
      "Para conferir, não: o romaneio chega por link no WhatsApp e abre no celular. Para acompanhar cargas e pagamentos, ele cria o acesso a partir desse mesmo link.",
    aberta: true,
  },
  {
    pergunta: "Funciona no celular, no campo com sinal fraco?",
    resposta:
      "Sim. As telas foram feitas para o celular e para registro rápido no bananal, com pouco sinal.",
    aberta: false,
  },
  {
    pergunta: "Sou corretor. Como começo?",
    resposta:
      "Se você já recebeu um romaneio pelo Rizoma, entre com o seu telefone. Se ainda não, fale com a gente e combinamos como trazer os seus produtores.",
    aberta: false,
  },
  {
    pergunta: "E a equipe e o manejo da fazenda?",
    resposta:
      "Equipe (diárias e folha) e Manejo (operações e irrigação) já estão em uso e são liberados sob convite.",
    aberta: false,
  },
  {
    pergunta: "Meus dados ficam seguros?",
    resposta:
      "Cada fazenda vê só os próprios dados. Tudo fica em banco de dados com backup automático e conexão criptografada.",
    aberta: false,
  },
] as const;

// ─── Rodapé ───────────────────────────────────────────────────────────────────

export const RODAPE = {
  copyright: "© 2026 Rizoma",
  links: [
    { href: "/termos", rotulo: "Termos de uso" },
    { href: "/privacidade", rotulo: "Privacidade" },
  ],
} as const;
